/**
 * X (Twitter) OAuth 2.0 PKCE authentication for Woodchuck game.
 *
 * Flow:
 * 1. Client generates code_verifier + code_challenge
 * 2. Redirects user to X authorization page
 * 3. X redirects back with auth code
 * 4. Client sends code + code_verifier to /api/x-exchange (Cloudflare Pages Function)
 * 5. Backend exchanges for token, fetches user profile, returns user data
 * 6. Client stores user data in localStorage
 *
 * Set REACT_APP_X_CLIENT_ID in your environment to enable.
 * Set REACT_APP_WOODCHUCK_HANDLE for the game's official X account (@woodchuckgame).
 */

const CLIENT_ID = process.env.REACT_APP_X_CLIENT_ID || '';
const WOODCHUCK_HANDLE = process.env.REACT_APP_WOODCHUCK_HANDLE || '@woodchuckgame';
const GAME_URL = process.env.REACT_APP_GAME_URL || 'https://howmuchwoodcouldawoodchuckchuck.com';
const STORAGE_KEY = 'woodchuck_x_auth';
const VERIFIER_KEY = 'woodchuck_x_verifier';
const STATE_KEY = 'woodchuck_x_state';
const SCOPES = 'tweet.read tweet.write users.read';

function getRedirectUri() {
  return window.location.origin;
}

function generateRandomString(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, b => chars[b % chars.length]).join('');
}

async function sha256Base64Url(plain) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const bytes = new Uint8Array(digest);
  let str = '';
  bytes.forEach(b => { str += String.fromCharCode(b); });
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

const XAuth = {
  isConfigured() {
    return CLIENT_ID.length > 0;
  },

  getUser() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      return data ? JSON.parse(data) : null;
    } catch {
      return null;
    }
  },

  getHandle() {
    const user = XAuth.getUser();
    return user ? user.handle : null;
  },

  getSessionToken() {
    const user = XAuth.getUser();
    return user ? user.sessionToken : null;
  },

  getAuthHeaders() {
    const token = XAuth.getSessionToken();
    if (!token) return {};
    return { 'Authorization': `Bearer ${token}` };
  },

  signOut() {
    localStorage.removeItem(STORAGE_KEY);
  },

  async startAuth() {
    if (!CLIENT_ID) {
      console.warn('X OAuth not configured: set REACT_APP_X_CLIENT_ID');
      return;
    }

    const codeVerifier = generateRandomString(64);
    const codeChallenge = await sha256Base64Url(codeVerifier);
    const state = generateRandomString(32);

    sessionStorage.setItem(VERIFIER_KEY, codeVerifier);
    sessionStorage.setItem(STATE_KEY, state);

    const params = new URLSearchParams({
      response_type: 'code',
      client_id: CLIENT_ID,
      redirect_uri: getRedirectUri(),
      scope: SCOPES,
      state: state,
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });

    window.location.href = `https://x.com/i/oauth2/authorize?${params.toString()}`;
  },

  async handleCallback() {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state');

    if (!code || !state) return null;

    const savedState = sessionStorage.getItem(STATE_KEY);
    if (state !== savedState) {
      console.error('X OAuth state mismatch');
      return null;
    }

    const codeVerifier = sessionStorage.getItem(VERIFIER_KEY);
    if (!codeVerifier) {
      console.error('X OAuth code verifier not found');
      return null;
    }

    window.history.replaceState({}, '', window.location.pathname);
    sessionStorage.removeItem(VERIFIER_KEY);
    sessionStorage.removeItem(STATE_KEY);

    try {
      const resp = await fetch('/api/x-exchange', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          code_verifier: codeVerifier,
          redirect_uri: getRedirectUri(),
        }),
      });

      if (!resp.ok) {
        console.error('X OAuth token exchange failed');
        return null;
      }

      const userData = await resp.json();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(userData));
      return userData;
    } catch (err) {
      console.error('X OAuth exchange error:', err);
      return null;
    }
  },

  /**
   * Compose a level completion share text.
   */
  composeLevelShareText(level, celebrityName, score, collectionCount, levelTime, kpm, handle, completionId) {
    const timeStr = levelTime ? `${levelTime.toFixed(1)}s` : '';
    const kpmStr = kpm ? `${kpm} KPM` : '';
    const speedLine = (timeStr || kpmStr)
      ? `Time: ${timeStr} | Speed: ${kpmStr}`
      : '';
    const now = new Date();
    const dateStr = `${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ${now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    const playerLine = handle ? `${handle} just` : 'I just';
    // Share URL with OG image card (X will show the screenshot)
    const shareUrl = completionId
      ? `${GAME_URL}/share/${completionId}`
      : GAME_URL;
    return [
      `${playerLine} cleared Stage ${level} - revealed ${celebrityName}!`,
      `Score: ${score} | Collection: ${collectionCount}/100`,
      speedLine,
      dateStr,
      `${WOODCHUCK_HANDLE}`,
      shareUrl,
      '#Woodchuck #Web3Gaming',
    ].filter(Boolean).join('\n');
  },

  /**
   * Compose a collection share text.
   */
  composeCollectionShareText(handle, collectionCount, topLevel) {
    return [
      `${handle} has collected ${collectionCount}/100 celebrities in Woodchuck!`,
      `Highest level: ${topLevel}`,
      `Can you beat my collection? ${WOODCHUCK_HANDLE}`,
      `${GAME_URL}`,
      '#Woodchuck #Web3Gaming',
    ].join('\n');
  },

  /**
   * Open X compose window with pre-filled text via web intent.
   * Returns the popup window reference.
   */
  openShareIntent(text) {
    const url = `https://x.com/intent/tweet?text=${encodeURIComponent(text)}`;
    return window.open(url, '_blank', 'width=550,height=450,noopener');
  },

  /**
   * Try to share via Web Share API with screenshot (mobile-friendly).
   * Falls back to X intent if Web Share is not available.
   * @param {string} text - Tweet text
   * @param {Blob|HTMLCanvasElement} blobOrCanvas - Pre-captured Blob or canvas element
   */
  async shareWithScreenshot(text, blobOrCanvas) {
    // Try Web Share API with image (mobile support)
    if (navigator.share && navigator.canShare) {
      try {
        // Accept either a pre-captured Blob or a canvas element
        let blob;
        if (blobOrCanvas instanceof Blob) {
          blob = blobOrCanvas;
        } else if (blobOrCanvas && blobOrCanvas.toBlob) {
          blob = await new Promise(resolve => blobOrCanvas.toBlob(resolve, 'image/png'));
        }

        if (blob) {
          const file = new File([blob], 'woodchuck-level.png', { type: blob.type || 'image/png' });
          const shareData = { text, files: [file] };

          if (navigator.canShare(shareData)) {
            await navigator.share(shareData);
            return true;
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Web Share failed, falling back to X intent');
        } else {
          return false; // User cancelled
        }
      }
    }

    // Fallback: X web intent (text only)
    XAuth.openShareIntent(text);
    return true;
  },

  /**
   * Post share to the woodchuck account feed via server function.
   * This notifies the woodchuck account about the player's achievement.
   */
  async notifyWoodchuckAccount(level, celebrityName, playerHandle, score) {
    try {
      await fetch('/api/x-share', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ level, celebrityName, playerHandle, score }),
      });
    } catch {
      // Silent fail — woodchuck account notification is best-effort
    }
  },
};

export default XAuth;
